/**
 * Base64 padrão (RFC 4648, com `=` de preenchimento) sobre Uint8Array, sem depender de
 * `btoa`/`atob` (que no Hermes e nos navegadores trabalham em "string binária", lentos e
 * fáceis de corromper com UTF-8) nem de `Buffer`. Usado pelo envelope v2 do backup
 * (ADR 0177): a cifra em hex dobrava o tamanho do arquivo; base64 custa um terço.
 *
 * Decodificação é ESTRITA: comprimento múltiplo de 4, só o alfabeto padrão, `=` só no fim.
 * Qualquer outra coisa lança — um `ct` mal formado não pode virar bytes "quase certos".
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const PAD = '=';

const LOOKUP = new Int16Array(128).fill(-1);
for (let i = 0; i < ALPHABET.length; i++) LOOKUP[ALPHABET.charCodeAt(i)] = i;

export function bytesToBase64(bytes: Uint8Array): string {
    let out = '';
    const full = bytes.length - (bytes.length % 3);
    for (let i = 0; i < full; i += 3) {
        const triple = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        out +=
            ALPHABET[(triple >>> 18) & 63] +
            ALPHABET[(triple >>> 12) & 63] +
            ALPHABET[(triple >>> 6) & 63] +
            ALPHABET[triple & 63];
    }
    const rest = bytes.length - full;
    if (rest === 1) {
        const b0 = bytes[full];
        out += ALPHABET[b0 >>> 2] + ALPHABET[(b0 & 3) << 4] + PAD + PAD;
    } else if (rest === 2) {
        const b0 = bytes[full];
        const b1 = bytes[full + 1];
        out += ALPHABET[b0 >>> 2] + ALPHABET[((b0 & 3) << 4) | (b1 >>> 4)] + ALPHABET[(b1 & 15) << 2] + PAD;
    }
    return out;
}

export function base64ToBytes(text: string): Uint8Array {
    if (text.length % 4 !== 0) throw new Error('base64: comprimento inválido');
    if (text.length === 0) return new Uint8Array(0);

    let padding = 0;
    if (text.endsWith(PAD + PAD)) padding = 2;
    else if (text.endsWith(PAD)) padding = 1;

    const outLength = (text.length / 4) * 3 - padding;
    const out = new Uint8Array(outLength);
    const body = text.length - padding;
    let o = 0;

    const sextet = (index: number): number => {
        const code = text.charCodeAt(index);
        const value = code < 128 ? LOOKUP[code] : -1;
        if (value < 0) throw new Error('base64: caractere inválido');
        return value;
    };

    for (let i = 0; i + 4 <= body; i += 4) {
        const triple = (sextet(i) << 18) | (sextet(i + 1) << 12) | (sextet(i + 2) << 6) | sextet(i + 3);
        out[o++] = (triple >>> 16) & 255;
        out[o++] = (triple >>> 8) & 255;
        out[o++] = triple & 255;
    }
    if (padding === 2) {
        const i = body - 2;
        const pair = (sextet(i) << 18) | (sextet(i + 1) << 12);
        out[o++] = (pair >>> 16) & 255;
    } else if (padding === 1) {
        const i = body - 3;
        const triple = (sextet(i) << 18) | (sextet(i + 1) << 12) | (sextet(i + 2) << 6);
        out[o++] = (triple >>> 16) & 255;
        out[o++] = (triple >>> 8) & 255;
    }
    return out;
}
